import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Menu, MenuItem, Sidebar, SubMenu} from 'react-pro-sidebar';
import SideBarSvgSelector from './SideBarSvgSelector';
import {useNavigate, useLocation} from 'react-router-dom';
import s from './ProSideBar.module.css';
import ExitButton from './ExitButton';
import {useTranslation} from 'react-i18next';
import {useStatusRestriction} from '../../hooks/useStatusRestriction';

interface SidebarNavElementsProps {
	onMenuItemClick?: () => void;
}

const OPENED_SUBMENU_KEY = 'openedSubMenus';

const SidebarNavElements: React.FC<SidebarNavElementsProps> = ({
	onMenuItemClick,
}) => {
	const restrictions = useStatusRestriction();
	const navigate = useNavigate();
	const location = useLocation();
	const {t} = useTranslation();
	const siteUrl = process.env.REACT_APP_DOMEIN_URL;
	const currentPath = location.pathname;

	// Определяем, является ли это мобильной версией
	const isMobile = !!onMenuItemClick;

	// Состояние открытых SubMenu - сохраняем в localStorage для десктопа
	const [openedSubMenus, setOpenedSubMenus] = useState<Set<string>>(() => {
		if (!isMobile) {
			try {
				const saved = localStorage.getItem(OPENED_SUBMENU_KEY);
				if (saved) {
					return new Set(JSON.parse(saved));
				}
			} catch (e) {
				console.error('Error loading opened submenus:', e);
			}
		}
		return new Set<string>();
	});

	// Флаг для отслеживания первого рендера (чтобы не сохранять при инициализации)
	const isFirstRender = useRef(true);

	// Сохраняем состояние открытых меню в localStorage при изменении
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		if (!isMobile) {
			try {
				if (openedSubMenus.size > 0) {
					localStorage.setItem(
						OPENED_SUBMENU_KEY,
						JSON.stringify(Array.from(openedSubMenus)),
					);
				} else {
					// Очищаем localStorage если все меню закрыты
					localStorage.removeItem(OPENED_SUBMENU_KEY);
				}
			} catch (e) {
				console.error('Error saving opened submenus:', e);
			}
		}
	}, [openedSubMenus, isMobile]);

	// Обработчики для открытия/закрытия SubMenu
	const handleSubMenuToggle = useCallback(
		(subMenuId: string, isOpen: boolean) => {
			if (!isMobile) {
				setOpenedSubMenus((prev) => {
					const newSet = new Set(prev);
					if (isOpen) {
						newSet.add(subMenuId);
					} else {
						newSet.delete(subMenuId);
					}
					return newSet;
				});
			}
		},
		[isMobile],
	);

	const handleNavigateHome = useCallback(() => {
		navigate('/');
		onMenuItemClick?.();
	}, [navigate, onMenuItemClick]);

	const handleNavigateRegistration = useCallback(() => {
		navigate('/registration_new_client');
		onMenuItemClick?.();
	}, [navigate, onMenuItemClick]);

	const handleNavigateNews = useCallback(() => {
		navigate('/news');
		onMenuItemClick?.();
	}, [navigate, onMenuItemClick]);

	const handleNavigateProfile = useCallback(() => {
		navigate('/personal_cabinet_update');
		onMenuItemClick?.();
	}, [navigate, onMenuItemClick]);

	const renderNavItem = useCallback(
		(to: string, label: string) => {
			const fullPath = `/${to}`;
			const isActive = currentPath === fullPath;

			const handleClick = () => {
				navigate(fullPath);
				onMenuItemClick?.();
			};

			return (
				<MenuItem
					key={to}
					className={`${s.subItem} ${isActive ? s.active : ''}`}
					onClick={handleClick}
					role="button"
					tabIndex={0}
				>
					{t(label)}
				</MenuItem>
			);
		},
		[currentPath, navigate, onMenuItemClick, t],
	);

	if (restrictions.isTerminateUser) {
		return (
			<Sidebar className={s.sidebar}>
				<Menu className={s.menuPage}>
					<MenuItem
						className={s.menuLink}
						icon={<SideBarSvgSelector id={'side_link_03'} />}
						onClick={handleNavigateHome}
					>
						{t('Головна')}
					</MenuItem>
					<ExitButton title={t('Вихід')} />
				</Menu>
			</Sidebar>
		);
	}

	return (
		<Sidebar className={s.sidebar}>
			<Menu className={s.menuPage}>
				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'home'} />}
					component="a"
					href={siteUrl || '/'}
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('До сайту')}
				</MenuItem>
				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_03'} />}
					onClick={handleNavigateHome}
				>
					{t('Головна')}
				</MenuItem>
				{restrictions.canAccessBusinessSection && (
					<SubMenu
						icon={<SideBarSvgSelector id={'side_link_01'} />}
						className={s.menuLink}
						label={t('Мій бізнес')}
						open={isMobile ? undefined : openedSubMenus.has('business')}
						onOpenChange={(isOpen) =>
							handleSubMenuToggle('business', isOpen)
						}
					>
						{restrictions.canAccessMyTeamPage &&
							renderNavItem('my_team', 'Моя команда')}
						{restrictions.canAccessNetworkIncomePage &&
							renderNavItem('my_bonus', 'Мій дохід з мережі')}
						{restrictions.canAccessPartnerIncomePage &&
							renderNavItem('my_accounts', 'Мій партнерський дохід')}

						{restrictions.canAccessMyOrdersPage &&
							renderNavItem('my_orders', 'Мої замовлення')}
						{/*{user?.status === 'manager' &&
							restrictions.canAccessMyOrdersPage &&
							renderNavItem('my_orders_manager', 'Мої замовлення Менеджер')}*/}
						{restrictions.canAccessDynamicPage &&
							renderNavItem('my_dynamic', 'Моя динаміка')}
					</SubMenu>
				)}

				{restrictions.canAccessWarehouseSection && (
					<SubMenu
						icon={<SideBarSvgSelector id={'side_link_01'} />}
						className={s.menuLink}
						label={t('Склад')}
						open={isMobile ? undefined : openedSubMenus.has('warehouse')}
						onOpenChange={(isOpen) =>
							handleSubMenuToggle('warehouse', isOpen)
						}
					>
						{renderNavItem('list_of_invoices', 'Список накладних')}
						{renderNavItem('list_of_products', 'Список товарів')}
						{renderNavItem('paperwork_list', 'Список оформлень')}
						{renderNavItem('customer_orders', 'Замовлення клієнтів')}
						{renderNavItem('product_report', 'Товарний звіт')}
						{renderNavItem('list_of_bonuses', 'Список бонусів')}
						{restrictions.canAccessCashOrders &&
							renderNavItem('cash_order_list', 'Список касових ордерів')}
						{restrictions.canAccessFinancialReport &&
							renderNavItem('financial_report', 'Фінансовий звіт')}
					</SubMenu>
				)}

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_03'} />}
					onClick={handleNavigateRegistration}
				>
					{t('Реєстрація партнера/клієнта')}
				</MenuItem>

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'news'} />}
					onClick={handleNavigateNews}
				>
					{t('Новини')}
				</MenuItem>

				{/*	<SubMenu
					icon={<SideBarSvgSelector id={'side_link_05'} />}
					className={s.menuLink}
					label={t('Навчання')}
				>
					{renderNavItem('main_education/5', 'SlimHub School')}
					{renderNavItem('main_education/6', 'Бізнес School')}
					{renderNavItem('main_education/7', 'Продукт School')}
				</SubMenu>*/}

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_08'} />}
					component="a"
					href="https://media.choice.ua/"
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('Медіабаза')}
				</MenuItem>

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_05'} />}
					component="a"
					href="https://akademiia-rozvitku-choice.academyocean.com/auth/sign/in"
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('Академія розвитку')}
				</MenuItem>

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_06'} />}
					component="a"
					href="http://t.me/bychoicebot"
					target="_blank"
					rel="noopener noreferrer"
				>
					{t('byCHOICE.BOT')}
				</MenuItem>

				<MenuItem
					className={s.menuLink}
					icon={<SideBarSvgSelector id={'side_link_09'} />}
					onClick={handleNavigateProfile}
				>
					{t('Особистий профіль')}
				</MenuItem>

				<ExitButton title={t('Вихід')} />
			</Menu>
		</Sidebar>
	);
};

export default SidebarNavElements;
